import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Paperclip, Send, AlertTriangle, X as Xicon, RefreshCcw, Loader2 } from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function attachmentUrl(a) {
    if (!a?.url) return "";
    if (a.url.startsWith("http")) return a.url;
    return `${BACKEND_URL}${a.url}`;
}

/**
 * Coach ↔ Client thread + composer with image + voice + retry/fallback.
 */
export default function MessageThread({ mode, clientId, api: apiInstance, otherName, onSent }) {
    const httpApi = apiInstance || api;
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [loadErr, setLoadErr] = useState("");
    const [attachments, setAttachments] = useState([]); // [{kind,url,name,mime,size,duration_sec}]
    const [uploading, setUploading] = useState(false);
    const [pending, setPending] = useState([]); // local-only items waiting for retry
    const fileRef = useRef(null);
    const bottomRef = useRef(null);

    const fetchUrl = mode === "coach" ? "/messages" : "/me/messages";
    const sendUrl = mode === "coach" ? "/messages" : "/me/messages";

    const load = async () => {
        try {
            const params = mode === "coach" ? { client_id: clientId } : {};
            const { data } = await httpApi.get(fetchUrl, { params });
            setMessages(data);
            setLoadErr("");
        } catch (e) {
            setLoadErr(formatApiError(e.response?.data?.detail) || e.message || "Couldn't load messages");
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, mode]);
    useEffect(() => {
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [clientId, mode]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, pending.length]);

    const uploadFile = async (blob, kind, filename, durationSec = 0) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, filename);
            const { data } = await httpApi.post(`/messages/upload?kind=${kind}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachments((a) => [...a, { ...data, duration_sec: durationSec }]);
        } catch (e) {
            alert(formatApiError(e.response?.data?.detail) || e.message || "Upload failed");
        } finally { setUploading(false); }
    };

    const onFilePicked = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFile(file, "image", file.name);
        e.target.value = "";
    };

    const onVoice = async (blob, duration) => {
        const ext = (blob.type.includes("mp4") ? "mp4" : "webm");
        await uploadFile(blob, "audio", `voice-${Date.now()}.${ext}`, Math.round(duration * 10) / 10);
    };

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body && attachments.length === 0) return;
        const pendingItem = {
            id: `tmp-${Date.now()}`,
            body, attachments: [...attachments],
            tmp: true, status: "sending",
            from_role: mode === "coach" ? "coach" : "client",
        };
        setPending((p) => [...p, pendingItem]);
        setBusy(true);
        setDraft(""); setAttachments([]);
        try {
            const { data } = await httpApi.post(sendUrl, {
                client_id: clientId,
                body,
                attachments: pendingItem.attachments,
            });
            setMessages((m) => [...m, data]);
            setPending((p) => p.filter((x) => x.id !== pendingItem.id));
            onSent && onSent();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || "Send failed";
            setPending((p) => p.map((x) => x.id === pendingItem.id ? { ...x, status: "failed", error: msg } : x));
        } finally { setBusy(false); }
    };

    const retry = async (item) => {
        setPending((p) => p.map((x) => x.id === item.id ? { ...x, status: "sending", error: "" } : x));
        try {
            const { data } = await httpApi.post(sendUrl, {
                client_id: clientId,
                body: item.body,
                attachments: item.attachments,
            });
            setMessages((m) => [...m, data]);
            setPending((p) => p.filter((x) => x.id !== item.id));
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || "Send failed";
            setPending((p) => p.map((x) => x.id === item.id ? { ...x, status: "failed", error: msg } : x));
        }
    };

    const discard = (item) => setPending((p) => p.filter((x) => x.id !== item.id));

    const renderAttachments = (atts, mine) => {
        if (!atts?.length) return null;
        return (
            <div className="mt-2 space-y-2">
                {atts.map((a, i) => a.kind === "image" ? (
                    <a key={i} href={attachmentUrl(a)} target="_blank" rel="noreferrer" className="block">
                        <img src={attachmentUrl(a)} alt="" className="max-w-full max-h-60 border border-[var(--border)]" />
                    </a>
                ) : a.kind === "audio" ? (
                    <audio key={i} src={attachmentUrl(a)} controls className="w-full" style={{ maxWidth: 260, filter: mine ? "invert(0)" : "invert(0.1)" }} />
                ) : (
                    <a key={i} href={attachmentUrl(a)} target="_blank" rel="noreferrer" className="text-xs underline font-mono">
                        📎 {a.name || "file"}
                    </a>
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full bg-[var(--surface)] border border-[var(--border)]" data-testid="message-thread">
            <div className="flex-1 overflow-auto p-5 space-y-3" data-testid="thread-list">
                {loadErr && (
                    <div className="flex items-center justify-between border border-[var(--danger)] bg-[var(--danger)]/10 p-3 text-xs text-[var(--danger)]" data-testid="thread-load-err">
                        <span className="inline-flex items-center gap-2"><AlertTriangle size={14} /> {loadErr}</span>
                        <button onClick={load} className="underline">Retry</button>
                    </div>
                )}
                {messages.length === 0 && pending.length === 0 && !loadErr && (
                    <p className="text-center text-[var(--text-muted)] text-sm py-12">No messages yet. Send the first one.</p>
                )}
                {messages.map((m) => {
                    const isMine = (mode === "coach" && m.from_role === "coach") || (mode === "client" && m.from_role === "client");
                    return (
                        <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                            <div className={`max-w-[75%] px-4 py-2.5 ${
                                isMine ? "bg-[var(--gold)] text-[#0a0908]" : "bg-[var(--surface-2)] text-[var(--text)] border border-[var(--border)]"
                            }`}>
                                {m.body && <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                                {renderAttachments(m.attachments, isMine)}
                                <p className={`text-[10px] font-mono mt-1 ${isMine ? "text-[#0a0908]/60" : "text-[var(--text-muted)]"}`}>
                                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </p>
                            </div>
                        </div>
                    );
                })}
                {pending.map((p) => (
                    <div key={p.id} className="flex justify-end" data-testid={`pending-${p.id}`}>
                        <div className={`max-w-[75%] px-4 py-2.5 ${p.status === "failed" ? "bg-[var(--danger)]/20 border border-[var(--danger)]" : "bg-[var(--gold)]/60 text-[#0a0908]"}`}>
                            {p.body && <p className="text-sm whitespace-pre-wrap">{p.body}</p>}
                            {renderAttachments(p.attachments, true)}
                            {p.status === "sending" && (
                                <p className="text-[10px] font-mono mt-1 inline-flex items-center gap-1">
                                    <Loader2 size={10} className="animate-spin" /> sending…
                                </p>
                            )}
                            {p.status === "failed" && (
                                <div className="text-[11px] font-mono mt-1 space-y-1">
                                    <p className="text-[var(--danger)]">Failed: {p.error}</p>
                                    <div className="flex gap-2">
                                        <button onClick={() => retry(p)} className="inline-flex items-center gap-1 underline" data-testid={`retry-${p.id}`}><RefreshCcw size={10} /> Retry</button>
                                        <button onClick={() => discard(p)} className="inline-flex items-center gap-1 underline text-[var(--text-muted)]"><Xicon size={10} /> Discard</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>

            {attachments.length > 0 && (
                <div className="border-t border-[var(--border)] p-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                    {attachments.map((a, i) => (
                        <div key={i} className="relative border border-[var(--border-bright)] p-1 bg-[var(--surface-2)]">
                            {a.kind === "image" ? (
                                <img src={attachmentUrl(a)} alt="" className="h-14" />
                            ) : (
                                <span className="text-xs font-mono px-2 py-1">🎙 {a.duration_sec ? `${Math.round(a.duration_sec)}s` : "voice"}</span>
                            )}
                            <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                                className="absolute -top-1 -right-1 bg-[var(--bg)] border border-[var(--border-bright)] p-0.5"
                                data-testid={`remove-att-${i}`}><Xicon size={10} /></button>
                        </div>
                    ))}
                </div>
            )}

            <form onSubmit={send} className="border-t border-[var(--border)] p-3 flex items-center gap-2" data-testid="thread-composer">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFilePicked} data-testid="thread-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="p-2 border border-[var(--border-bright)] text-[var(--text-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)]"
                    data-testid="thread-attach" title="Attach image">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                </button>
                <VoiceRecorder onRecorded={onVoice} disabled={uploading} />
                <input
                    className="input-dark flex-1"
                    placeholder={`Message ${otherName || "…"}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    data-testid="thread-input"
                />
                <button type="submit" disabled={busy || uploading || (!draft.trim() && attachments.length === 0)}
                    className="btn-primary inline-flex items-center gap-1" data-testid="thread-send">
                    <Send size={14} /> {busy ? "…" : "Send"}
                </button>
            </form>
        </div>
    );
}
